import React from 'react';
import LegalLayout from './LegalLayout';

const EFFECTIVE_DATE = "1 February 2026";
const SUPPORT_EMAIL = "miiglobalpartner@gmail.com";

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      metaTitle="Privacy Policy | TelegramEmailBot"
      metaDescription="Read the TelegramEmailBot privacy policy. We explain what data we collect, how we use it, and your rights. Plain English, no legal tricks."
      canonicalPath="/privacy"
      testid="privacy-page"
    >
      <p className="text-sm text-gray-500"><em>Effective date: {EFFECTIVE_DATE}</em></p>

      <p>
        This Privacy Policy explains what information <strong>TelegramEmailBot</strong> ("we", "us", or "the Service") — operated by Mii Global Partner Holdings Sdn. Bhd. — collects, why we collect it, and how you can control it. We've written this in plain English. If anything is unclear, email us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>

      <h2>1. Information we collect</h2>
      <p>To make the Service work, we collect the following:</p>
      <ul>
        <li><strong>Telegram chat ID and basic profile</strong> — provided to us by Telegram when you start the bot. We use this to deliver emails to you.</li>
        <li><strong>The unique email address we assign you</strong> — required so we know which Telegram chat to forward incoming emails to.</li>
        <li><strong>Email messages routed through the Service</strong> — the full content (sender, subject, body, attachments) of emails sent to or from your assigned address. We store these only as long as needed to deliver them to your Telegram, then keep a short transactional history for debugging.</li>
        <li><strong>Subscription and billing data</strong> — handled by Stripe. We see your subscription status, the masked card brand, and the country of issue. We <em>never</em> see your full card number.</li>
        <li><strong>Basic technical logs</strong> — IP address, timestamps and request paths. Used for security, abuse detection and uptime monitoring.</li>
      </ul>

      <h2>2. How we use your information</h2>
      <p>We use the data above strictly to:</p>
      <ul>
        <li>Deliver emails between your assigned address and your Telegram chat</li>
        <li>Process subscription payments and renewal reminders</li>
        <li>Prevent abuse, spam and unauthorized access</li>
        <li>Respond to support requests</li>
        <li>Comply with applicable laws and lawful requests</li>
      </ul>
      <p>We do <strong>not</strong> sell your data, share it with advertisers, or use the content of your emails to train AI models. AI summaries (where enabled) process the message in memory and discard it immediately.</p>

      <h2>3. Third-party processors</h2>
      <p>We rely on the following providers to operate the Service:</p>
      <ul>
        <li><strong>Telegram</strong> — message delivery to your chat</li>
        <li><strong>Brevo</strong> — outbound email sending</li>
        <li><strong>Google (Gmail API)</strong> — inbound email reception for our routing infrastructure</li>
        <li><strong>Stripe</strong> — subscription billing and PCI-compliant card handling</li>
        <li><strong>MongoDB Atlas</strong> — encrypted database hosting</li>
        <li><strong>Google Gemini</strong> — optional AI email summarization (text only, no storage by us)</li>
      </ul>
      <p>Each provider has its own privacy policy. We choose providers with strong data-protection standards.</p>

      <h2>4. Data retention</h2>
      <ul>
        <li>Email content is stored only as long as needed for reliable delivery, typically up to 30 days for troubleshooting.</li>
        <li>Account and subscription records are kept while your account is active and for up to 24 months after cancellation for accounting/tax reasons.</li>
        <li>You can request immediate deletion at any time by emailing us — we comply within 30 days unless required by law to retain longer.</li>
      </ul>

      <h2>5. Your rights</h2>
      <p>Depending on where you live (GDPR, CCPA, PDPA Malaysia, etc.) you have the right to:</p>
      <ul>
        <li>Access a copy of your data</li>
        <li>Correct inaccurate data</li>
        <li>Request deletion of your data</li>
        <li>Export your data in a portable format</li>
        <li>Object to or restrict certain processing</li>
        <li>Withdraw consent at any time</li>
      </ul>
      <p>Send any of these requests to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll handle it within 30 days.</p>

      <h2>6. Security</h2>
      <p>
        We use HTTPS everywhere, encrypted database storage, role-based access controls, and follow the principle of least privilege internally. That said, no system is 100% secure. If you believe your account has been compromised, contact us immediately.
      </p>

      <h2>7. Children</h2>
      <p>The Service is not intended for users under 13. We do not knowingly collect data from children under 13.</p>

      <h2>8. International transfers</h2>
      <p>
        Our servers and processors operate in multiple regions including the EU, US and Southeast Asia. By using the Service you consent to your data being processed in these regions, with appropriate safeguards in place.
      </p>

      <h2>9. Changes to this policy</h2>
      <p>
        We'll update this policy if our practices change. Material changes will be announced via our Telegram support channel and at the top of this page. Continued use of the Service after an update means you accept the new terms.
      </p>

      <h2>10. Contact</h2>
      <p>
        Mii Global Partner Holdings Sdn. Bhd.<br />
        17, Taman Bukit Dumbar, Jelutong, Penang 11600, Malaysia<br />
        Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </p>
    </LegalLayout>
  );
}
